import { z } from 'zod';
import { Building2, Video, MessageSquare } from 'lucide-react';

// Appointment form schema
export const appointmentSchema = z.object({
  patient_id: z.string().min(1, 'Patient is required'),
  practitioner_id: z.string().min(1, 'Provider is required'),
  service_type_id: z.string().optional(),
  // Optional priced AppointmentType for billing. When set, the API
  // auto-creates an invoice and emails the patient a pay link.
  appointment_type_id: z.string().optional(),
  location_id: z.string().optional(),
  date: z.string().min(1, 'Date is required'),
  start_time: z.string().min(1, 'Start time is required'),
  duration: z
    .number({
      required_error: 'Duration is required',
      invalid_type_error: 'Duration is required',
    })
    .int('Duration must be a whole number')
    .min(15, 'Duration must be at least 15 minutes')
    .max(480, 'Duration cannot exceed 8 hours (480 minutes)'),
  status: z.enum(
    [
      'scheduled',
      'confirmed',
      'checked_in',
      'in_progress',
      'completed',
      'no_show',
      'cancelled',
      'rescheduled',
    ],
    {
      required_error: 'Status is required',
      invalid_type_error: 'Please select a valid status',
    }
  ),
  title: z.string().optional(),
  notes: z.string().max(2000, 'Provider notes cannot exceed 2000 characters').optional(),
  patient_notes: z.string().max(1000, 'Patient instructions cannot exceed 1000 characters').optional(),
  visit_mode: z.enum(['in_person', 'video', 'async_message']).optional(),
  is_telehealth: z.boolean().optional(),
});

export type AppointmentFormData = z.infer<typeof appointmentSchema>;

// Standard appointment titles
export const APPOINTMENT_TITLES = [
  { value: 'Initial Consultation', label: 'Initial Consultation' },
  { value: 'Follow-up Visit', label: 'Follow-up Visit' },
  { value: 'Weight Loss Consultation', label: 'Weight Loss Consultation' },
  { value: 'Lab Review', label: 'Lab Review' },
  { value: 'Prescription Refill', label: 'Prescription Refill' },
  { value: 'Hormone Consultation', label: 'Hormone Consultation' },
  { value: 'Wellness Check', label: 'Wellness Check' },
  { value: 'Urgent Visit', label: 'Urgent Visit' },
  { value: 'Medication Review', label: 'Medication Review' },
  { value: 'New Patient Intake', label: 'New Patient Intake' },
] as const;

// Visit mode configuration
export const VISIT_MODES = [
  {
    value: 'in_person' as const,
    label: 'In-Person',
    icon: Building2,
    description: 'Patient visits the clinic',
    color: 'blue',
  },
  {
    value: 'video' as const,
    label: 'Video Visit',
    icon: Video,
    description: 'Real-time video consultation',
    color: 'green',
  },
  {
    value: 'async_message' as const,
    label: 'Async Consult',
    icon: MessageSquare,
    description: 'Secure messaging consultation',
    color: 'purple',
  },
] as const;

export type VisitMode = (typeof VISIT_MODES)[number]['value'];

